import React, { useEffect, useState } from 'react';
import { Gift, Check, ArrowRight, Loader2, Home } from 'lucide-react';
import { processDirectGiftPayment } from '../services/firestore';
import { auth } from '../services/firebase';

const GiftSuccess = ({ onNavigate }) => {
    // Removed react-router-dom hooks
    const [status, setStatus] = useState('processing'); // processing, success, error
    const [errorMsg, setErrorMsg] = useState('');
    const [giftDetails, setGiftDetails] = useState(null);

    useEffect(() => {
        // Use URLSearchParams directly
        const params = new URLSearchParams(window.location.search);
        const sessionId = params.get('session_id');

        if (!sessionId) {
            setStatus('error');
            setErrorMsg("No session ID found.");
            return;
        }

        const verifyGift = async () => {
            try {
                // Wait for auth to be ready
                const user = auth.currentUser;
                if (!user) {
                    // Try waiting briefly? Or assume App.js handled auth wait
                    // App.js renders this only if (user) exists usually, 
                    // BUT for the external redirect, App.js logic shows 'gift-success' tab based on URL param.
                    // However, App.js ALSO waits for onAuthChange before setting loading=false.
                    // So user SHOULD be ready.
                    setStatus('error');
                    setErrorMsg("Please sign in to complete verification.");
                    return;
                }

                console.log("Verifying gift for session:", sessionId);
                const result = await processDirectGiftPayment(user.uid, sessionId);

                setGiftDetails(result);
                setStatus('success');

                // Clear URL params to avoid re-triggering logic on refresh?
                // window.history.replaceState({}, document.title, window.location.pathname);
                // Actually, let's leave it for now or user might lose the "Success" view on refresh.
            } catch (err) {
                console.error("Verification failed:", err);
                setErrorMsg(err.message || "Payment verification failed.");
                setStatus('error');
            }
        };

        verifyGift();
    }, []);

    const handleReturn = () => {
        // Clear URL params before navigating cleanly
        window.history.replaceState({}, document.title, window.location.pathname);
        if (onNavigate) {
            onNavigate('profile');
        } else {
            // Fallback
            window.location.href = '/';
        }
    };

    return (
        <div className="page-container animate-fade-in" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80vh' }}>
            <div className="glass-panel" style={{ padding: '40px', maxWidth: '500px', width: '90%', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>

                {status === 'processing' && (
                    <>
                        <Loader2 size={48} className="spin" style={{ color: 'var(--primary)', marginBottom: '20px' }} />
                        <h2>Finalizing Gift...</h2>
                        <p style={{ color: 'var(--text-muted)' }}>Confirming payment and generating code.</p>
                        <p style={{ fontSize: '12px', opacity: 0.7 }}>This may take a few seconds.</p>
                    </>
                )}

                {status === 'success' && (
                    <>
                        <div style={{
                            width: '80px', height: '80px', borderRadius: '50%',
                            background: 'rgba(16, 185, 129, 0.2)', color: '#10b981',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            margin: '0 auto 20px auto'
                        }}>
                            <Check size={40} />
                        </div>
                        <h2 style={{ color: '#10b981', marginBottom: '10px' }}>Gift Sent!</h2>
                        <p style={{ fontSize: '1.2rem', marginBottom: '5px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                            Your gift has been delivered! <Gift size={24} color="#FFD700" strokeWidth={1.5} />
                        </p>
                        <p style={{ fontSize: '1.1rem', color: 'var(--primary)', fontWeight: 'bold', marginBottom: '20px' }}>
                            Gift for: {giftDetails?.recipientName || auth.currentUser?.displayName || "You"}
                        </p>

                        <div style={{ background: 'rgba(255,255,255,0.05)', padding: '15px', borderRadius: '12px', marginBottom: '30px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                                <span style={{ color: 'var(--text-muted)' }}>Code ID:</span>
                                <span style={{ fontFamily: 'monospace' }}>{giftDetails?.code}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ color: 'var(--text-muted)' }}>Status:</span>
                                <span style={{ color: '#10b981' }}>Active</span>
                            </div>
                        </div>

                        <button onClick={handleReturn} className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }}>
                            <Home size={18} style={{ marginRight: '8px' }} /> Return to Profile
                        </button>
                    </>
                )}

                {status === 'error' && (
                    <>
                        <div style={{ color: '#ef4444', marginBottom: '20px' }}>Error</div>
                        <h2>Something went wrong</h2>
                        <p style={{ color: 'var(--text-muted)', marginBottom: '20px' }}>{errorMsg}</p>
                        <button onClick={() => onNavigate ? onNavigate('support') : window.location.reload()} className="btn" style={{ width: '100%', justifyContent: 'center' }}>
                            Try Again or Contact Support
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

export default GiftSuccess;
